import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type ToastType = 'success' | 'error' | 'warning';

export interface UseToastResult {
  showToast: (message: string, type?: ToastType) => void;
  toastElement: React.ReactElement | null;
}

const VISIBLE_MS = 4700;
const REMOVE_MS = 5000;
const SLIDE_OUT_MS = REMOVE_MS - VISIBLE_MS;

const GLYPH: Record<ToastType, string> = { success: 'tick', error: 'close', warning: 'warning' };

/**
 * The app's one toast: top-right, 5s lifecycle (visible 4.7s, slide-out 0.3s), closable with X.
 * Styling: `toast toast--verify` in `assets/scss/toast.scss`.
 *
 * Returns a `toastElement` JSX node — render it once near the page root.
 */
const useToast = (): UseToastResult => {
  const { t } = useTranslation();
  const [toast, setToast] = useState<string | null>(null);
  const [toastType, setToastType] = useState<ToastType>('success');
  const [toastHiding, setToastHiding] = useState(false);
  const hideRef = useRef<NodeJS.Timeout | null>(null);
  const removeRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimers = () => {
    if (hideRef.current) clearTimeout(hideRef.current);
    if (removeRef.current) clearTimeout(removeRef.current);
  };

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    clearTimers();
    setToastHiding(false);
    setToastType(type);
    setToast(message);
    hideRef.current = setTimeout(() => setToastHiding(true), VISIBLE_MS);
    removeRef.current = setTimeout(() => setToast(null), REMOVE_MS);
  }, []);

  const closeToast = () => {
    clearTimers();
    setToastHiding(true);
    removeRef.current = setTimeout(() => setToast(null), SLIDE_OUT_MS);
  };

  useEffect(() => clearTimers, []);

  const toastElement = toast ? (
    <div
      role="status"
      className={`toast toast--verify toast--${toastType}${toastHiding ? ' toast--hiding' : ''}`}
    >
      <span className="toast__icon" aria-hidden="true">
        <span className="toast__spark toast__spark--1" />
        <span className="toast__spark toast__spark--2" />
        <span className="toast__spark toast__spark--3" />
        <span className={`toast__glyph ico--${GLYPH[toastType]} ico--sm`} />
      </span>
      <span className="toast__message">{toast}</span>
      <button type="button" className="toast__close" aria-label={t('close')} onClick={closeToast}>
        <span className="ico--close ico--sm" />
      </button>
    </div>
  ) : null;

  return { showToast, toastElement };
};

export default useToast;
